import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { rx } from "../lib/api";
import { Pill, Plus, Trash2, CalendarDays, Clock, Flame } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import PrescriptionSettingsPanel from "../components/PrescriptionSettingsPanel";

function countStats(doses) {
  const now = Date.now();
  let taken = 0, pending = 0, upcoming = 0;
  for (const d of doses) {
    if (d.status === "taken" || d.status === "auto_taken") taken++;
    else if (d.status === "pending") {
      pending++;
      const t = new Date(d.scheduled_at).getTime();
      if (t - now > 0 && t - now < 24 * 3600 * 1000) upcoming++;
    }
  }
  return { taken, pending, upcoming };
}

function NextDoseBadge({ doses }) {
  const next = doses.find(d => d.status === "pending");
  if (!next) return <span className="dose-pill taken">Course complete</span>;
  const t = new Date(next.scheduled_at);
  const diff = (t.getTime() - Date.now()) / 60000;
  const label = diff < 60 && diff > -5 ? "Due soon" : t.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" });
  return <span className={`dose-pill ${diff < 60 && diff > -5 ? "due-soon" : "pending"}`}><Clock className="h-3 w-3" /> {label}</span>;
}

export default function PrescriptionsPage() {
  const [list, setList] = useState([]);
  const [details, setDetails] = useState({}); // id -> {doses}
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const items = await rx.list();
      setList(items);
      const map = {};
      for (const it of items) {
        const d = await rx.get(it.id);
        map[it.id] = d.doses;
      }
      setDetails(map);
    } catch (e) {
      toast.error("Failed to load prescriptions");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id) {
    if (!window.confirm("Delete this prescription and its schedule?")) return;
    try {
      await rx.remove(id);
      toast.success("Prescription removed");
      load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  }

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <span className="label-caps">Prescription Manager</span>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter text-[color:var(--text-main)]">Your courses.</h1>
          <p className="mt-2 text-[color:var(--text-muted)] max-w-xl">Photograph a label, confirm the schedule, let Handy nudge you through the course.</p>
        </div>
        <Link to="/prescriptions/new" data-testid="new-prescription-button">
          <Button className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white rounded-lg px-5 h-11">
            <Plus className="h-4 w-4 mr-2" /> Add Prescription
          </Button>
        </Link>
      </div>

      <PrescriptionSettingsPanel />

      {loading ? (
        <div className="text-[color:var(--text-muted)]">Loading…</div>
      ) : list.length === 0 ? (
        <div className="card-tactile rounded-2xl p-10 flex flex-col items-center text-center" data-testid="empty-prescriptions">
          <div className="h-16 w-16 rounded-full bg-[color:var(--primary-soft)] flex items-center justify-center mb-4">
            <Pill className="h-7 w-7 text-[color:var(--primary-ink)]" />
          </div>
          <h3 className="font-display font-bold text-xl">No prescriptions yet</h3>
          <p className="text-sm text-[color:var(--text-muted)] mt-1 max-w-sm">Add your first one. We'll read the label and build a schedule you can follow and export.</p>
          <Link to="/prescriptions/new" className="mt-5">
            <Button className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white rounded-lg h-11 px-5" data-testid="empty-add-rx">
              <Plus className="h-4 w-4 mr-2" /> Add your first prescription
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {list.map((item, idx) => {
            const d = details[item.id] || [];
            const stats = countStats(d);
            const progress = d.length ? Math.round((stats.taken / d.length) * 100) : 0;
            return (
              <div
                key={item.id}
                data-testid={`rx-card-${item.id}`}
                className="card-tactile rounded-2xl p-6 animate-fade-up flex flex-col"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-bold text-xl tracking-tight text-[color:var(--text-main)]">{item.medicine_name}</h3>
                    <p className="text-sm text-[color:var(--text-muted)] mt-1">
                      {(() => {
                        const bands = item.tapering_schedule;
                        if (bands && bands.length > 1) {
                          return `${bands[0].dose_amount} → ${bands[bands.length - 1].dose_amount} · tapering · ${item.duration_days} days`;
                        }
                        return `${item.dosage} · ${item.frequency_per_day}x/day · ${item.duration_days} days`;
                      })()}
                    </p>
                  </div>
                  <button onClick={() => handleDelete(item.id)} data-testid={`rx-delete-${item.id}`} className="text-[color:var(--text-muted)] hover:text-[color:var(--accent-ink)] transition-colors p-1">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="mt-5 flex items-center gap-2 flex-wrap">
                  <NextDoseBadge doses={d} />
                  {item.adherence && item.adherence.total_past > 0 && (
                    <>
                      <span className="dose-pill taken" data-testid={`rx-adherence-${item.id}`} title="Adherence">
                        {item.adherence.adherence_pct}% adherence
                      </span>
                      {item.adherence.current_streak >= 2 && (
                        <span className="dose-pill auto-taken" data-testid={`rx-streak-${item.id}`} title="Current streak">
                          <Flame className="h-3 w-3" /> {item.adherence.current_streak}
                        </span>
                      )}
                    </>
                  )}
                  <span className="dose-pill pending">{stats.pending} pending</span>
                </div>

                <div className="mt-5">
                  <div className="h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                    <div className="h-full bg-[color:var(--primary)] transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-[color:var(--text-muted)] mt-2">
                    <span>{progress}% complete</span>
                    <span>Started {new Date(item.start_datetime).toLocaleDateString()}</span>
                  </div>
                </div>

                <Link to={`/prescriptions/${item.id}`} data-testid={`rx-open-${item.id}`} className="mt-6">
                  <Button variant="outline" className="w-full rounded-lg h-11 border-[color:var(--border-subtle)] text-[color:var(--text-main)] hover:bg-[color:var(--surface-2)]">
                    <CalendarDays className="h-4 w-4 mr-2" /> Open schedule
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
